/**
 * analytics.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Client-side Google Analytics 4 event helpers.
 *
 * Uses react-ga4 (initialized in index.js via ReactGA.initialize).
 * No manual script tag in index.html required.
 *
 * Wire-up summary:
 *   pageView    — App.jsx useEffect on location.pathname
 *   viewItem    — RestaurantProductDialog, Product.jsx on open
 *   addToCart   — all handleAddItem handlers
 *   beginCheckout — all ShippingScreen components on mount
 *   purchase    — all PlaceOrderScreen on success
 *   search      — SearchResultsPage after results load
 */

import ReactGA from 'react-ga4';
import { CURRENCY } from '../config/market';

// ── Core fire helper ──────────────────────────────────────────────────────────

function fire(eventName, params) {
  try {
    ReactGA.event(eventName, params);
  } catch (_) {}
}

// ── Item builder ─────────────────────────────────────────────────────────────

/**
 * Build a GA4-compatible item object.
 *
 * Accepts EITHER:
 *   buildItem({ id, name, price, vendorName, category, quantity })  — flat form
 *   buildItem(product, vendor, qty)                                  — legacy form
 *
 * @param {object} productOrFlat
 * @param {object} [vendor]
 * @param {number} [qty=1]
 */
function buildItem(productOrFlat, vendor, qty = 1) {
  const p = productOrFlat;
  // Flat-object form: { id, name, price, vendorName, ... }
  if (p.id !== undefined || p.vendorName !== undefined) {
    return {
      item_id:       String(p.id || p._id || ''),
      item_name:     p.name       || '',
      item_brand:    p.vendorName || 'Lusuku',
      item_category: p.category   || p.vendorType || 'General',
      price:         p.price      || 0,
      quantity:      p.quantity   || qty,
      currency:      CURRENCY,
    };
  }
  // Legacy form: (product, vendor, qty)
  return {
    item_id:       String(p._id || p.productId || ''),
    item_name:     p.name       || '',
    item_brand:    vendor?.name || vendor?.tradingName || 'Lusuku',
    item_category: p.category   || p.vendorType || 'General',
    price:         p.price      || 0,
    quantity:      qty,
    currency:      CURRENCY,
  };
}

// ── Exported event functions ──────────────────────────────────────────────────

/**
 * Track a page view — call on every route change.
 * @param {string} path  e.g. '/restaurant/shop/abc'
 */
export const pageView = (path) => {
  try {
    ReactGA.send({ hitType: 'pageview', page: path });
  } catch (_) {}
};

/**
 * Track a vendor/shop view with a stable, URL-independent identity.
 *
 * This is the durable replacement for path-based vendor attribution: it emits
 * a `vendor_view` event carrying `vendor_id` (the vendor's Mongo id) and
 * `vendor_type`, so backend analytics can attribute traffic to a shop without
 * parsing the URL slug or relying on the (race-prone) page title. Register
 * `vendor_id` and `vendor_type` as event-scoped GA4 custom dimensions to use
 * them in reports.
 *
 * Fire this once per shop view, AFTER the vendor record has loaded (so the id
 * is known). Pass the vendor-model `_id` when available; the backend also
 * tolerates the owner id.
 *
 * @param {object} args
 * @param {string} args.vendorId    - Vendor Mongo id (model _id preferred)
 * @param {string} [args.vendorType]- 'restaurant' | 'healthcare' | 'retailer' | 'wholesaler' | 'brand'
 * @param {string} [args.path]      - Page path; defaults to current pathname
 */
export const vendorView = ({ vendorId, vendorType, path } = {}) => {
  if (!vendorId) return;
  fire('vendor_view', {
    vendor_id:   String(vendorId),
    vendor_type: vendorType || 'unknown',
    page_path:   path || (typeof window !== 'undefined' ? window.location.pathname : ''),
  });
};

/**
 * Track a product detail view.
 * Accepts flat object { id, name, price, vendorName } OR (product, vendor).
 * @param {object} productOrFlat
 * @param {object} [vendor]
 */
export const viewItem = (productOrFlat, vendor) => {
  const item = buildItem(productOrFlat, vendor);
  fire('view_item', {
    currency: CURRENCY,
    value:    item.price,
    items:    [item],
  });
};

/**
 * Track an add-to-cart event.
 * Accepts flat object { id, name, price, quantity, vendorName } OR (product, vendor, qty).
 * @param {object} productOrFlat
 * @param {object} [vendor]
 * @param {number} [qty=1]
 */
export const addToCart = (productOrFlat, vendor, qty = 1) => {
  const item = buildItem(productOrFlat, vendor, qty);
  fire('add_to_cart', {
    currency: CURRENCY,
    value:    item.price * item.quantity,
    items:    [item],
  });
};

/**
 * Track checkout initiation (ShippingScreen mount).
 * @param {Array<{product, vendor, qty}>} cartItems
 * @param {number} total
 */
export const beginCheckout = (cartItems = [], total = 0) =>
  fire('begin_checkout', {
    currency: CURRENCY,
    value:    total,
    items:    cartItems.map(({ product, vendor, qty = 1 }) =>
                buildItem(product, vendor, qty)),
  });

/**
 * Track a completed purchase (PlaceOrderScreen on success).
 * @param {string} orderId
 * @param {Array<{product, vendor, qty}>} cartItems
 * @param {number} total
 */
export const purchase = (orderId, cartItems = [], total = 0) =>
  fire('purchase', {
    transaction_id: String(orderId),
    currency:       CURRENCY,
    value:          total,
    items:          cartItems.map(({ product, vendor, qty = 1 }) =>
                      buildItem(product, vendor, qty)),
  });

/**
 * Track a search query.
 * Accepts flat object { searchTerm, resultCount } OR (query, resultCount).
 * @param {string|object} queryOrFlat
 * @param {number} [resultCount=0]
 */
export const search = (queryOrFlat, resultCount = 0) => {
  const term  = typeof queryOrFlat === 'object'
    ? queryOrFlat.searchTerm  || ''
    : queryOrFlat;
  const count = typeof queryOrFlat === 'object'
    ? queryOrFlat.resultCount ?? resultCount
    : resultCount;
  fire('search', { search_term: term, result_count: count });
};

// ── Growth events ────────────────────────────────────────────────────────────
//
// One vocabulary for web and both customer apps (the apps' analytics module
// carries the same names), so every growth change can be measured the same way
// on every platform. Each change in the growth log names the event(s) that
// prove it. Register the params below as event-scoped custom dimensions in GA4.
//
// surface:     'home' | 'storefront' | 'product' | 'cart' | 'checkout' |
//              'order_done' | 'account' | 'referral'
// vendor_type: 'restaurant' | 'shop' | 'brand' | 'pharmacy' | 'lusuku'

const cleanGrowthParams = (params = {}) =>
  Object.entries(params).reduce((clean, [key, value]) => {
    if (value === undefined || value === null) return clean;
    if (typeof value === 'number') {
      if (Number.isFinite(value)) clean[key] = value;
    } else if (typeof value === 'boolean') {
      // Sent as 'true' / 'false', as the apps do: Firebase on Android drops a
      // real boolean, and one shape keeps web and app reports comparable.
      clean[key] = value ? 'true' : 'false';
    } else {
      clean[key] = String(value).trim().slice(0, 100);
    }
    return clean;
  }, {});

export const growthEvent = (eventName, params) =>
  fire(eventName, cleanGrowthParams(params));

/* Events already sent this page load, for the "seen" events that should count
   once per view rather than once per render. */
const growthEventsSent = new Set();
export const growthEventOnce = (onceKey, eventName, params) => {
  if (growthEventsSent.has(onceKey)) return;
  growthEventsSent.add(onceKey);
  growthEvent(eventName, params);
};

export const trackPromiseView = (surface) =>
  growthEventOnce(`promise_view:${surface}`, 'promise_view', { surface });
export const trackPromisePolicyOpen = (surface) =>
  growthEvent('promise_policy_open', { surface });
export const trackHelplineTap = (surface, channel) =>
  growthEvent('helpline_tap', { surface, channel });
/* A tap on one of the homepage's two buttons, with which one it was and
   where it stood, so the meal-time lead order can be judged. */
export const trackHeroAction = (surface, action, position) =>
  growthEvent('hero_action_tap', { surface, action, position });
export const trackLocationPromptView = (surface, trigger) =>
  growthEvent('location_prompt_view', { surface, trigger });
export const trackLocationSet = (surface, method) =>
  growthEvent('location_set', { surface, method });
export const trackDeliveryEstimateView = ({ surface, vendorType, fee, waivedAmount, offer }) =>
  growthEvent('delivery_estimate_view', {
    surface,
    vendor_type: vendorType,
    fee,
    waived_amount: waivedAmount || 0,
    offer: offer || '',
  });
export const trackDeliveryEstimateUnavailable = ({ surface, vendorType, reason }) =>
  growthEvent('delivery_estimate_unavailable', { surface, vendor_type: vendorType, reason });
export const trackMinOrderReached = ({ surface, vendorType, subtotal }) =>
  growthEvent('min_order_reached', { surface, vendor_type: vendorType, subtotal });
export const trackSignInPrompt = (surface) =>
  growthEvent('sign_in_prompt', { surface });
export const trackLogin = (method) => growthEvent('login', { method });
export const trackSignUp = (method) => growthEvent('sign_up', { method });
// `item_count`, not `items`: GA4 reserves `items` for the ecommerce item list.
export const trackReorder = ({ surface, vendorType, itemCount, orderId }) =>
  growthEvent('reorder', { surface, vendor_type: vendorType, item_count: itemCount, order_id: orderId });
export const trackOfferView = (surface, offer) =>
  growthEventOnce(`offer_view:${surface}:${offer}`, 'offer_view', { surface, offer });
export const trackOfferApplied = ({ offer, waivedAmount, vendorType }) =>
  growthEvent('offer_applied', { offer, waived_amount: waivedAmount, vendor_type: vendorType });
export const trackReferralShare = (surface, channel) =>
  growthEvent('referral_share', { surface, channel });
export const trackReferralLinkOpen = (hasCode) =>
  growthEvent('referral_link_open', { has_code: Boolean(hasCode) });
export const trackAppPromptView = (surface) =>
  growthEventOnce(`app_prompt_view:${surface}`, 'app_prompt_view', { surface });
export const trackAppPromptTap = (surface, store) =>
  growthEvent('app_prompt_tap', { surface, store });

// Order together (group baskets). The apps send the same names and params.
// The basket's own changes (group_basket_locked, group_order_placed,
// group_basket_cancelled, group_share_paid) come from
// utils/groupBasketView transitionEvents and go through growthEvent as is.
//   mode:   'HOST_PAYS_ALL' | 'EACH_PAYS_SHARE'
//   rail:   'momo' | 'airtel' | 'zamtel' | 'card'
export const trackGroupBasketCreate = ({ mode, surface }) =>
  growthEvent('group_basket_create', { mode, surface });
export const trackGroupBasketJoin = (hasAccount) =>
  growthEvent('group_basket_join', { has_account: Boolean(hasAccount) });

/**
 * Tie GA4 activity to the Lusuku account (GA4 User-ID), so a shopper who
 * orders on the web and later signs in on the app is one person in reports.
 * Pass null on sign-out. platform is a user property the apps set too.
 */
export const setAnalyticsUser = (userId) => {
  try {
    ReactGA.set({
      user_id: userId ? String(userId) : null,
      user_properties: { platform: 'web' },
    });
  } catch (_) {}
};

/* Which surface a path belongs to, for growth events raised by components that
   appear on many pages (the location prompt, the location tracking). */
export const surfaceForPath = (pathname = '') => {
  const path = String(pathname || '');
  if (path === '/' || path === '') return 'home';
  if (path === '/cart') return 'cart';
  if (/shipping|placeorder|payment|ticket-checkout/i.test(path)) return 'checkout';
  if (/\/product\//.test(path) || /^\/brand\/product\//.test(path)) return 'product';
  if (/^\/(restaurant\/shop|shop|healthcare\/shop|brand\/shop)\//.test(path)) return 'storefront';
  if (/^\/(my_profile|orders|tickets|support)/.test(path)) return 'account';
  if (/^\/invite/.test(path)) return 'referral';
  return 'other';
};

/* userLocation.locationSource to the location_set method vocabulary. */
export const LOCATION_SOURCE_METHOD = { gps: 'gps', manual: 'search', ip: 'ip_suggestion', saved: 'saved' };
